import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useHostname, SsrNavigate } from "@/lib/ssr";
import { isNewHomeEnabled } from "@/lib/homeFlag";
import NotFound from "@/pages/NotFound";
import Index from "@/pages/Index";
import HomePage from "./HomePage";
import StudioPage from "./StudioPage";
import PersonalPage from "./PersonalPage";
import DownloadPage from "./DownloadPage";
import { APP_PATH, STUDIO_PATH, keepLang } from "./paths";

/** Pages that exist only where the new home is on (staging until launch); a real 404 elsewhere. */
export const NewHomeOnly = ({ children }: { children: ReactNode }) =>
  isNewHomeEnabled(useHostname()) ? <>{children}</> : <NotFound />;

/** "/" on the app host: the new home where it's on, otherwise today's home page, unchanged. */
export const HomeRoute = () => {
  const host = useHostname();
  const { search } = useLocation();
  if (!isNewHomeEnabled(host)) return <Index />;
  // The old product switch's links: "/?for=business" is /studio and "/?for=personal" is /personal (one URL per
  // page); ?lang= is kept.
  const pick = new URLSearchParams(search).get("for");
  if (pick === "business") return <SsrNavigate to={`${STUDIO_PATH}${keepLang(search)}`} replace permanent />;
  if (pick === "personal") return <SsrNavigate to={`${APP_PATH}${keepLang(search)}`} replace permanent />;
  return <HomePage />;
};

/** "/studio": Photohawk Studio, for photographers and event businesses, where the new home is on. */
export const StudioRoute = () => (
  <NewHomeOnly>
    <StudioPage />
  </NewHomeOnly>
);

/** "/events" and "/business" (the Studio page's URLs before): a 301 to /studio, keeping ?lang=. The SSR server
 *  answers these first (src/lib/seo/redirects.ts); this covers navigation inside the app. A 404 where the new home
 *  is off. */
export const OldStudioRedirect = () => {
  const { search, hash } = useLocation();
  return (
    <NewHomeOnly>
      <SsrNavigate to={`${STUDIO_PATH}${keepLang(search)}${hash}`} replace permanent />
    </NewHomeOnly>
  );
};

/** "/app": a 301 to /personal (the app page's canonical URL), keeping ?lang=. The SSR server answers it first
 *  (src/lib/seo/redirects.ts); this covers navigation inside the app. A 404 where the new home is off. */
export const AppAliasRedirect = () => {
  const { search, hash } = useLocation();
  return (
    <NewHomeOnly>
      <SsrNavigate to={`${APP_PATH}${keepLang(search)}${hash}`} replace permanent />
    </NewHomeOnly>
  );
};

/** "/personal": the Photohawk app, the phone app for your own photos, where the new home is on. */
export const PersonalRoute = () => (
  <NewHomeOnly>
    <PersonalPage />
  </NewHomeOnly>
);

/** "/download": the Photohawk Studio app's download page, where the new home is on. */
export const DownloadRoute = () => (
  <NewHomeOnly>
    <DownloadPage />
  </NewHomeOnly>
);
